import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ChevronDownSmallIcon } from '@inter-ikea-kompis/icons';
import type { ITheme } from '@inter-ikea-kompis/types';
import { SafariWriteDirectionUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Icon } from '@inter-ikea-kompis/component-icon';
import { Text } from '@inter-ikea-kompis/component-text';

import QuantityStyle from './QuantityStyle.js';

/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 *
 * @event change Emits when the quantity value changes
 */

export default class Quantity extends KompisElement {
	// Set the select to disabled state.
	@property({ type: Boolean })
	public disabled = false;

	// Minimum selectable value.
	@property({ type: Number })
	public min = 0;

	// Maximum selectable value.
	@property({ type: Number })
	public max = 100;

	// Set the value of the field.
	@property({ type: Number })
	public value = 0;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	private hasFocus = false;
	private pointerIsDown = false;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		const direction = SafariWriteDirectionUtility.getWriteDirection(this);
		const values: number[] = [];
		for (let i = this.min; i <= this.max; i++) {
			values.push(i);
		}

		return html`
			<style>
				${QuantityStyle.getStyle({
					isDisabled: this.disabled,
					hasFocus: this.hasFocus,
					direction: direction,
					max: this.max,
					theme: this.theme
				})}
			</style>
			<div class="container">
				<div class="dropdown">
					<div class="outline"></div>
					<select
						@change="${this.onChange.bind(this)}"
						.disabled="${this.disabled}"
						@pointerdown="${this.onPointerDown.bind(this)}"
						@focusout="${this.onFocusOut.bind(this)}"
						@focus="${this.onFocus.bind(this)}"
						tabindex="0"
					>
						${values.map(
							(value) => html`<option ?selected="${value === this.value}">${value}</option>`
						)}
					</select>
					${this.getChevronIcon()}
				</div>
			</div>
		`;
	}
	public override connectedCallback(): void {
		this.customElements.define('kompis-icon', Icon);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}

	/**
	 * Runs when change event occurs.
	 *
	 * @param event HTMLEvent.
	 */
	private onChange(event: Event): void {
		const target = <HTMLSelectElement>event.target;
		const value = Number(target.value);

		this.dispatchEvent(
			new CustomEvent('change', {
				bubbles: true,
				detail: {
					value
				}
			})
		);
	}

	/**
	 * Gets the chevron icon.
	 *
	 * @returns Template.
	 */
	private getChevronIcon(): RenderResult {
		return html`
			<kompis-icon .theme="${this.theme}" .icon="${ChevronDownSmallIcon}"></kompis-icon>
		`;
	}

	/**
	 * HTML event handler.
	 */
	private onFocusOut(): void {
		this.hasFocus = false;
		this.pointerIsDown = false;
		this.update();
	}

	/**
	 * HTML event handler.
	 */
	private onFocus(): void {
		if (this.pointerIsDown) {
			return;
		}
		this.hasFocus = true;
		this.update();
	}

	/**
	 * HTML event handler.
	 */
	private onPointerDown(): void {
		this.pointerIsDown = true;
		if (this.hasFocus) {
			this.hasFocus = false;
			this.update();
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-quantity': Quantity;
	}
}
